/**
 * USER-SEND-COMMUNICATION.MODAL
 * Account-wide communication email editor
 */
import { useState } from "react"
import { withTranslation, WithTranslation } from "react-i18next"
import { connect } from "react-redux"
import { toast } from "react-toastify"
import Button from "@/components/button"
import Modal from "@/components/modal"
import ModalConfirm from "@/components/modal-confirm"
import { store } from "@/index"
import { sessionEditAccountOptions } from "@/redux/_session.actions"
import { STATUS_LOADED, STATUS_SAVING } from "@/redux/_status.types"
import { Session } from "@/redux/_session.types"
import { accountUpdateOptions } from "@/redux/account.actions"
import { AccountOptions } from "@/redux/account.types"
import { Email } from "@/redux/email.types"
import { UserState } from "@/redux/user.types"
import {
  userFetchCommunicationRecipients,
  userSendCommunication,
  userSendCommunicationTest
} from "@/redux/user.actions"
import ProjectEmailContentWidget from "@/widgets/project-email-content.widget"
import i18n from "@/translate/i18n"
import { faClipboardList, faUndo, faUsers } from "@fortawesome/free-solid-svg-icons"
import { validateEmail } from "@/utils/validate-email.utils"
import CardButton from "@/components/card-button"
import Checkbox from "@/components/checkbox"
import Link from "@/components/link"
import ListItem from "@/components/list-item"
import CardContainer from "@/components/card-container"
import Dropdown from "@/components/dropdown"
import { Survey } from "@/redux/survey.types"
import { surveyFetch } from "@/redux/survey.actions"

interface StateProps extends WithTranslation {
  _session: Session
  user: UserState
}

interface OwnProps {
  onClose: Function
}

type Props = StateProps & OwnProps

const MODAL_CONFIRM_SEND_ALL = "MODAL_CONFIRM_SEND_ALL"
const MODAL_SEND_RECIPIENTS = "MODAL_SEND_RECIPIENTS"
const MODAL_SEND_SURVEY = "MODAL_SEND_SURVEY"
const MODAL_SEND_SURVEY_RECIPIENTS = "MODAL_SEND_SURVEY_RECIPIENTS"
const MODAL_SEND_TESTERS = "MODAL_SEND_TESTERS"

type SendMode = "users" | "survey"

function UserSendCommunicationModal(props: Props) {
  const { t } = props

  const [communicationEmail, setCommunicationEmail] = useState<Email>(
    normalizeCommunicationEmail(
      props._session.accountOptions.communicationEmail
        ? new Email(props._session.accountOptions.communicationEmail)
        : getDefaultCommunicationEmail()
    )
  )
  const [savedCommunicationEmail, setSavedCommunicationEmail] = useState<Email>(
    normalizeCommunicationEmail(new Email(JSON.parse(JSON.stringify(communicationEmail))))
  )
  const [currentModal, setCurrentModal] = useState<string | null>(null)
  const [status, setStatus] = useState(STATUS_LOADED)
  const [testers, setTesters] = useState<string[]>(
    props._session.accountOptions.communicationEmailTesters?.length
      ? props._session.accountOptions.communicationEmailTesters
      : props._session.email
        ? [props._session.email]
        : []
  )
  const [savedTesters, setSavedTesters] = useState<string[]>(testers)
  const [testersInput, setTestersInput] = useState(testers.join("\n"))

  //Recipients of the communication : every collaborator or the participants of a survey
  const [sendMode, setSendMode] = useState<SendMode>("users")
  const [surveys, setSurveys] = useState<Survey[]>([])
  const [currentSurvey, setCurrentSurvey] = useState<Survey | null>(null)
  const [surveyRecipients, setSurveyRecipients] = useState<string[]>([])
  const [displaySurveysTest, setDisplaySurveysTest] = useState(false)

  function getDefaultCommunicationEmail(): Email {
    return new Email().getCommunication(props._session.accountName)
  }

  function normalizeCommunicationEmail(email: Email): Email {
    const content = JSON.parse(JSON.stringify(email.content))

    return new Email({
      ...email,
      content: {
        ...content,
        cta: content.cta ?? {},
        link: content.link ?? {},
        info: content.info ?? {},
        linkSecondary: content.linkSecondary ?? {},
        footer: null
      }
    })
  }

  function getEmailPayload(email = communicationEmail): Email {
    const normalizedEmail = normalizeCommunicationEmail(email)

    return {
      id: normalizedEmail.id,
      name: normalizedEmail.name,
      sender: normalizedEmail.sender,
      isChecked: normalizedEmail.isChecked,
      content: {
        cta: normalizedEmail.content.cta,
        link: normalizedEmail.content.link,
        title: normalizedEmail.content.title,
        body: normalizedEmail.content.body,
        info: normalizedEmail.content.info,
        linkSecondary: normalizedEmail.content.linkSecondary,
        footer: null,
        subject: normalizedEmail.content.subject
      },
      Project: undefined
    } as unknown as Email
  }

  function getEmailSignature(email = communicationEmail): string {
    return JSON.stringify(getEmailPayload(email))
  }

  function getTestersSignature(testerEmails = testers): string {
    return JSON.stringify([...testerEmails].sort())
  }

  function getTesterEmails(value = testersInput): string[] {
    return value
      .split(/[\s,;]+/)
      .map((email) => email.trim())
      .filter((email) => email.length > 0)
  }

  function getInvalidTesterEmails(value = testersInput): string[] {
    return getTesterEmails(value).filter((email) => !validateEmail(email))
  }

  function resetTemplate() {
    setCommunicationEmail(normalizeCommunicationEmail(getDefaultCommunicationEmail()))
  }

  function isEmailDirty() {
    return getEmailSignature(communicationEmail) !== getEmailSignature(savedCommunicationEmail)
  }

  function isTestersDirty(testerEmails = testers) {
    return getTestersSignature(testerEmails) !== getTestersSignature(savedTesters)
  }

  function isDirty() {
    return isEmailDirty() || isTestersDirty()
  }

  function isTesterModalDirty() {
    return isEmailDirty() || isTestersDirty(getTesterEmails())
  }

  async function saveEmail(nextTesters: string[] = testers) {
    setStatus(STATUS_SAVING)
    const emailPayload = getEmailPayload()

    const options = new AccountOptions({
      ...props._session.accountOptions,
      communicationEmail: emailPayload,
      communicationEmailTesters: nextTesters
    })

    const response: any = await store.dispatch(accountUpdateOptions(options))

    if (!response.error) {
      store.dispatch(sessionEditAccountOptions("communicationEmail", emailPayload))
      store.dispatch(sessionEditAccountOptions("communicationEmailTesters", nextTesters))
      setSavedCommunicationEmail(new Email(emailPayload))
      setSavedTesters(nextTesters)
      toast(t("utils_saved"))
    }

    setStatus(STATUS_LOADED)
    return response
  }

  //Load surveys in order to pick the participants of one of them
  async function loadSurveys() {
    if (surveys.length > 0) return
    const response: any = await store.dispatch(surveyFetch())
    if (!response.error) setSurveys(response.map((survey) => new Survey(survey)))
  }

  function openRecipients() {
    if (isDirty()) return
    loadSurveys()
    setCurrentModal(MODAL_SEND_RECIPIENTS)
  }

  //Count the recipients of the survey before asking for confirmation
  async function selectSurvey(survey: Survey) {
    setSendMode("survey")
    setCurrentSurvey(survey)

    setStatus(STATUS_SAVING)
    const response: any = await store.dispatch(userFetchCommunicationRecipients(survey.id))
    setStatus(STATUS_LOADED)

    if (!response.error) {
      setSurveyRecipients(response.emails)
      setCurrentModal(MODAL_CONFIRM_SEND_ALL)
    }
  }

  async function sendEmail(isTest: boolean) {
    if (isDirty()) return

    //The email is read from the account options when sent
    //Save the default template if nothing has been saved yet
    if (!props._session.accountOptions.communicationEmail) {
      const saveResponse: any = await saveEmail()
      if (saveResponse.error) return
    }

    const surveyId = !isTest && sendMode === "survey" ? currentSurvey?.id : null
    if (!isTest && sendMode === "survey" && !surveyId) return

    setStatus(STATUS_SAVING)
    const action = isTest ? userSendCommunicationTest() : userSendCommunication(surveyId)
    const response: any = await store.dispatch(action)

    if (!response.error) {
      toast(
        t(
          isTest
            ? "user_send_communication_testers_done"
            : sendMode === "survey"
              ? "user_send_communication_survey_done"
              : "user_send_communication_all_done"
        )
      )
      props.onClose()
    }

    setStatus(STATUS_LOADED)
  }

  function sendTesters() {
    const testerEmails = getTesterEmails()
    if (testerEmails.length === 0 || getInvalidTesterEmails().length > 0 || isTesterModalDirty()) return
    sendEmail(true)
  }

  async function saveTesters() {
    const testerEmails = getTesterEmails()
    if (!isTesterModalDirty() || getInvalidTesterEmails().length > 0) return
    setTesters(testerEmails)
    const response: any = await saveEmail(testerEmails)
    if (!response.error) setCurrentModal(null)
  }

  return (
    <Modal
      isLarge
      isCloseButtonVisible
      title={t("user_send_communication")}
      status={status}
      onClose={() => props.onClose()}
      footer={
        <div className="flex gap-10">
          <Button onClick={() => resetTemplate()} icon={faUndo}></Button>
          <Button
            isWithBorder
            isFullWidth
            className={isDirty() ? "primary" : ""}
            classNameCustom={!isDirty() ? "inactive" : ""}
            onClick={() => {
              if (isDirty()) saveEmail()
            }}
            isLoading={status === STATUS_SAVING}>
            {t("utils_save")}
          </Button>
          <Button
            isFullWidth
            isWithBorder
            classNameCustom={isDirty() ? "inactive" : ""}
            onClick={() => {
              if (!isDirty()) setCurrentModal(MODAL_SEND_TESTERS)
            }}>
            {t("user_send_communication_testers")}
          </Button>
          <Button
            isFullWidth
            isWithBorder
            className={!isDirty() ? "primary" : ""}
            classNameCustom={isDirty() ? "inactive" : ""}
            onClick={() => openRecipients()}>
            {t("utils_next")}
          </Button>
        </div>
      }>
      {currentModal === MODAL_SEND_RECIPIENTS && (
        <Modal
          isCloseButtonVisible
          title={t("user_send_communication_recipients")}
          status={status}
          onClose={() => setCurrentModal(null)}>
          <CardContainer>
            <CardButton
              isCta
              icon={faUsers}
              title={t("user_send_communication_recipients_users")}
              subtitle={props.user.count + " " + t("users").toLowerCase()}
              onClick={() => {
                setSendMode("users")
                setCurrentSurvey(null)
                setCurrentModal(MODAL_CONFIRM_SEND_ALL)
              }}
            />

            <CardButton
              icon={faClipboardList}
              isActive={sendMode === "survey"}
              title={t("user_send_communication_recipients_survey")}
              onClick={() => {
                setSendMode("survey")
                setCurrentModal(MODAL_SEND_SURVEY)
              }}
            />
          </CardContainer>

          <div className="height-60" />
        </Modal>
      )}

      {currentModal === MODAL_SEND_SURVEY && (
        <Modal
          isCloseButtonVisible
          title={t("user_send_communication_recipients_survey")}
          status={status}
          onClose={() => setCurrentModal(MODAL_SEND_RECIPIENTS)}>
          <Checkbox
            active={displaySurveysTest}
            text={t("user_send_communication_survey_test")}
            onClick={() => setDisplaySurveysTest(!displaySurveysTest)}
          />

          <Dropdown
            isFullWidth
            active={currentSurvey?.id}
            displayField="name"
            list={displaySurveysTest ? surveys : surveys.filter((survey) => !survey.isTest)}
            maxHeight={160}
            title={t("user_send_communication_survey_select")}
            value={currentSurvey?.name}
            onSelect={(survey: Survey) => selectSurvey(survey)}
          />

          {/** margin below the dropdown in order to display the list of surveys */}
          <div className="height-200" />
        </Modal>
      )}

      {currentModal === MODAL_CONFIRM_SEND_ALL && (
        <ModalConfirm
          status={status}
          onNo={() => setCurrentModal(sendMode === "survey" ? MODAL_SEND_SURVEY : MODAL_SEND_RECIPIENTS)}
          onYes={() => sendEmail(false)}
          textBold={
            sendMode === "survey"
              ? t("user_send_communication_survey_confirm", {
                  count: surveyRecipients.length,
                  name: currentSurvey?.name
                })
              : t("user_send_communication_all_confirm", { count: props.user.count })
          }>
          {sendMode === "survey" && (
            <>
              <p className="grey-t">{t("user_send_communication_survey_recipients_help")}</p>

              <Link isWithoutMargin onClick={() => setCurrentModal(MODAL_SEND_SURVEY_RECIPIENTS)}>
                {t("user_send_communication_survey_recipients")}
              </Link>
            </>
          )}
        </ModalConfirm>
      )}

      {currentModal === MODAL_SEND_SURVEY_RECIPIENTS && (
        <Modal
          isCloseButtonVisible
          title={t("user_send_communication_survey_recipients")}
          onClose={() => setCurrentModal(MODAL_CONFIRM_SEND_ALL)}>
          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            {surveyRecipients.map((email) => (
              <ListItem key={email} isSmall>
                {email}
              </ListItem>
            ))}
          </div>
        </Modal>
      )}

      {currentModal === MODAL_SEND_TESTERS && (
        <Modal
          isCloseButtonVisible
          title={t("user_send_communication_testers")}
          status={status}
          onClose={() => setCurrentModal(null)}
          footer={
            <>
              <Button
                isWithBorder
                className={isTesterModalDirty() ? "primary" : ""}
                classNameCustom={!isTesterModalDirty() || getInvalidTesterEmails().length > 0 ? "inactive" : ""}
                onClick={() => saveTesters()}
                isLoading={status === STATUS_SAVING}>
                {t("utils_save")}
              </Button>
              <Button
                className={!isTesterModalDirty() ? "primary" : ""}
                classNameCustom={
                  getTesterEmails().length === 0 || getInvalidTesterEmails().length > 0 || isTesterModalDirty()
                    ? "inactive"
                    : ""
                }
                onClick={() => sendTesters()}
                isLoading={status === STATUS_SAVING}>
                {t("user_send_communication_testers")}
              </Button>
            </>
          }>
          <div className="text-input text-input-fullwidth">
            <div className="text-input-title flex">
              <span className="grey-t">{t("user_send_communication_testers_emails")}</span>
            </div>

            <textarea
              autoFocus
              rows={8}
              value={testersInput}
              onChange={(e) => setTestersInput(e.target.value)}
              style={{
                resize: "none",
                height: 160,
                width: "calc(100% - 20px)",
                border: testersInput.length > 0 && getInvalidTesterEmails().length > 0 ? "1px solid #eb5a46" : "none",
                padding: "6px 10px",
                backgroundColor: "#eaeaea",
                borderRadius: 8,
                margin: "6px 0px"
              }}
            />

            <div className="text-input-help">{t("user_send_communication_testers_emails_help")}</div>
          </div>

          {getInvalidTesterEmails().length > 0 && (
            <p className="red-t">
              {t("utils_email_invalid")} : {getInvalidTesterEmails().join(", ")}
            </p>
          )}

          <div className="height-100" />
        </Modal>
      )}

      <ProjectEmailContentWidget
        currentLanguage={i18n.language}
        emailItem={communicationEmail}
        isEditable
        isLinkEditable
        onEmailChange={(email) => setCommunicationEmail(normalizeCommunicationEmail(email))}
        skin={null}
      />
    </Modal>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  user: state.user
})

export default connect(mapStateToProps)(withTranslation()(UserSendCommunicationModal))
